import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  QualityClassBreakdown,
  QualityDailyPoint,
  QualityScoreRow,
  QualityScoresResponse,
  QualityTeamDailyPoint,
  QualityTier,
} from '@marnic/shared';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { ErrorState } from '../components/ErrorState';
import { ChartSkeleton, TableSkeleton } from '../components/Skeleton';

// ─── Date range presets ──────────────────────────────────────────────────

const PRESETS: { label: string; days: number }[] = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

interface RangeState {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

function defaultRange(): RangeState {
  const now = new Date();
  const past = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return { from: ymd(past), to: ymd(now) };
}

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangePreset(days: number): RangeState {
  const now = new Date();
  return { from: ymd(new Date(now.getTime() - days * 24 * 60 * 60 * 1000)), to: ymd(now) };
}

function rangeToParams(r: RangeState): string {
  const from = new Date(`${r.from}T00:00:00Z`).toISOString();
  const to = new Date(`${r.to}T23:59:59Z`).toISOString();
  return `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
}

// ─── Top-level view ───────────────────────────────────────────────────────

export function QualityScoresView(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const [range, setRange] = useState<RangeState>(defaultRange);
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  // All hooks run unconditionally — gating happens via `enabled` so the
  // rules-of-hooks are preserved even when a non-admin somehow lands here.
  const query = useQuery({
    queryKey: ['quality', 'scores', range],
    queryFn: () => api.get<QualityScoresResponse>(`/quality/scores${rangeToParams(range)}`),
    enabled: isAdmin,
  });

  const persistRun = useMutation({
    mutationFn: () =>
      api.post<QualityScoresResponse>(`/quality/scores/run`, {
        from: new Date(`${range.from}T00:00:00Z`).toISOString(),
        to: new Date(`${range.to}T23:59:59Z`).toISOString(),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quality', 'scores'] }),
  });

  // Defensive: the parent Route already gates /reports/* to admin, but if the
  // gating ever moves we want this page to refuse to render data anyway.
  if (!isAdmin) {
    return (
      <div className="card p-6 text-center text-sm text-ink-600">
        This page is restricted to administrators.
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        message={(query.error as { code?: string })?.code ?? "We couldn't load quality scores."}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const data = query.data;

  return (
    <div className="space-y-5">
      <Intro />
      <RangeControls
        range={range}
        onRange={setRange}
        onPersistRun={() => persistRun.mutate()}
        running={persistRun.isPending}
      />

      {query.isLoading || !data ? (
        <>
          <ChartSkeleton />
          <div className="card overflow-hidden">
            <TableSkeleton rows={6} cols={11} />
          </div>
        </>
      ) : (
        <>
          <HeadlineSummary data={data} />
          <TeamDailyChart data={data.teamDaily} />
          {data.scores.length === 0 ? (
            <EmptyState skipped={data.skippedLowVolume} />
          ) : (
            <ScoresTable scores={data.scores} />
          )}
        </>
      )}
    </div>
  );
}

function Intro(): JSX.Element {
  return (
    <section className="card p-4 text-sm leading-relaxed text-ink-700">
      <h2 className="text-base font-semibold text-ink-900">
        Quota-gaming detection — how to read this page
      </h2>
      <p className="mt-2">
        For every outbound call in the selected window, we classify it as a real
        live conversation or as a "gamed" attempt — voicemail, no answer,
        machine, or a silent connect where the prospect side never actually
        spoke. We then score each agent on six independent signals (S1–S6) and
        roll them into a composite <strong>QGS</strong> between 0 and 1:
      </p>
      <ul className="ml-5 mt-2 list-disc space-y-1 text-ink-600">
        <li><strong>Tier green</strong> (QGS &lt; 0.30): within normal range.</li>
        <li><strong>Tier yellow</strong> (0.30 – 0.55): soft review recommended.</li>
        <li><strong>Tier red</strong> (≥ 0.55): escalate for manager review.</li>
      </ul>
      <p className="mt-2 text-ink-600">
        Click any agent's row to open their full assessment: a per-day chart of
        their gaming pattern, breakdown by class, and a plain-English summary
        explaining what to do about them. The chart at the top shows the same
        thing at the team level — useful for spotting which days everyone
        struggled (probably a list/timing issue) versus which days specific
        agents stand out.
      </p>
    </section>
  );
}

function RangeControls({
  range,
  onRange,
  onPersistRun,
  running,
}: {
  range: RangeState;
  onRange: (r: RangeState) => void;
  onPersistRun: () => void;
  running: boolean;
}): JSX.Element {
  return (
    <section className="card flex flex-wrap items-end gap-3 p-4">
      <div className="flex flex-col">
        <label className="text-xs font-medium uppercase tracking-wide text-ink-500" htmlFor="quality-from">
          From
        </label>
        <input
          id="quality-from"
          type="date"
          value={range.from}
          max={range.to}
          onChange={(e) => onRange({ ...range, from: e.target.value })}
          className="input"
        />
      </div>
      <div className="flex flex-col">
        <label className="text-xs font-medium uppercase tracking-wide text-ink-500" htmlFor="quality-to">
          To
        </label>
        <input
          id="quality-to"
          type="date"
          value={range.to}
          min={range.from}
          onChange={(e) => onRange({ ...range, to: e.target.value })}
          className="input"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.days}
            type="button"
            className="btn-secondary"
            onClick={() => onRange(rangePreset(p.days))}
          >
            Last {p.label}
          </button>
        ))}
      </div>
      <div className="ml-auto">
        <button
          type="button"
          className="btn-primary"
          disabled={running}
          onClick={onPersistRun}
        >
          {running ? 'Saving…' : 'Save this window'}
        </button>
      </div>
    </section>
  );
}

function HeadlineSummary({ data }: { data: QualityScoresResponse }): JSX.Element {
  const teamGamedRatio = data.totalCalls > 0 ? data.totalGamed / data.totalCalls : 0;
  const red = data.scores.filter((s) => s.tier === 'red').length;
  const yellow = data.scores.filter((s) => s.tier === 'yellow').length;
  const green = data.scores.filter((s) => s.tier === 'green').length;
  return (
    <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
      <SummaryCard label="Window" value={`${data.windowDays} day${data.windowDays === 1 ? '' : 's'}`} hint={`${new Date(data.windowStart).toLocaleDateString()} → ${new Date(data.windowEnd).toLocaleDateString()}`} />
      <SummaryCard label="Outbound calls" value={data.totalCalls.toLocaleString()} />
      <SummaryCard label="Team gamed %" value={pct(teamGamedRatio)} accent={teamGamedRatio > 0.6 ? 'text-red-700' : teamGamedRatio > 0.45 ? 'text-amber-700' : 'text-emerald-700'} hint={`${data.totalGamed.toLocaleString()} of ${data.totalCalls.toLocaleString()} calls`} />
      <SummaryCard label="Scoreable agents" value={data.scores.length.toString()} hint={data.skippedLowVolume > 0 ? `${data.skippedLowVolume} skipped (under 20 calls)` : undefined} />
      <SummaryCard
        label="Tiers"
        value={`${red} R · ${yellow} Y · ${green} G`}
        accent={red > 0 ? 'text-red-700' : yellow > 0 ? 'text-amber-700' : 'text-emerald-700'}
      />
    </section>
  );
}

function SummaryCard({ label, value, accent, hint }: { label: string; value: string; accent?: string; hint?: string }): JSX.Element {
  return (
    <div className="card p-3">
      <div className="text-xs uppercase tracking-wide text-ink-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${accent ?? 'text-ink-800'}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-ink-500">{hint}</div>}
    </div>
  );
}

// ─── Team daily chart ─────────────────────────────────────────────────────

function TeamDailyChart({ data }: { data: QualityTeamDailyPoint[] }): JSX.Element {
  const width = Math.max(720, data.length * 56);
  const height = 220;
  const maxTotal = Math.max(1, ...data.map((d) => d.total));

  return (
    <section className="card p-4">
      <header className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-ink-800">Team gaming by day</h3>
        <p className="text-xs text-ink-500">
          Bars = total outbound calls. Red overlay = portion classified as gamed (voicemail / no-answer / silent). Hover for the exact ratio.
        </p>
      </header>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="h-56 min-w-full">
          {/* Grid */}
          {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
            const y = 30 + (1 - frac) * (height - 70);
            return (
              <g key={frac}>
                <line x1={50} x2={width - 10} y1={y} y2={y} stroke="#e5e7eb" strokeWidth={1} />
                <text x={45} y={y + 3} textAnchor="end" className="fill-ink-500 text-[10px]">{Math.round(frac * maxTotal)}</text>
              </g>
            );
          })}
          {data.map((d, i) => {
            const x = 60 + (i * (width - 80)) / Math.max(1, data.length);
            const barW = Math.max(8, (width - 80) / Math.max(1, data.length) - 6);
            const totalH = (d.total / maxTotal) * (height - 70);
            const gamedH = (d.gamed / maxTotal) * (height - 70);
            const baseY = height - 40;
            const dayLabel = d.date.slice(5);
            return (
              <g key={d.date}>
                <title>{`${d.date}\nTotal: ${d.total}\nGamed: ${d.gamed} (${pct(d.gamedRatio)})`}</title>
                <rect x={x} y={baseY - totalH} width={barW} height={totalH} fill="#bfdbfe" />
                <rect x={x} y={baseY - gamedH} width={barW} height={gamedH} fill="#ef4444" opacity={0.85} />
                <text x={x + barW / 2} y={height - 22} textAnchor="middle" className="fill-ink-500 text-[10px]">{dayLabel}</text>
                <text x={x + barW / 2} y={height - 8} textAnchor="middle" className="fill-ink-400 text-[9px]">{pct(d.gamedRatio)}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <ChartLegend items={[
        { color: '#bfdbfe', label: 'Total outbound' },
        { color: '#ef4444', label: 'Gamed (no real conversation)' },
      ]} />
    </section>
  );
}

function ChartLegend({ items }: { items: { color: string; label: string }[] }): JSX.Element {
  return (
    <div className="mt-2 flex flex-wrap gap-3 text-xs text-ink-600">
      {items.map((i) => (
        <div key={i.label} className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ background: i.color }} />
          <span>{i.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Scores table ─────────────────────────────────────────────────────────

function ScoresTable({ scores }: { scores: QualityScoreRow[] }): JSX.Element {
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <section className="card overflow-hidden">
      <header className="border-b border-ink-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-ink-800">Per-agent scores</h3>
        <p className="text-xs text-ink-500">Click a row to expand the full assessment with chart and plain-English explanation.</p>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full whitespace-nowrap text-sm">
          <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="px-4 py-2">Agent</th>
              <th className="px-4 py-2 text-right">Calls</th>
              <th className="px-4 py-2 text-right">Gamed</th>
              <th className="px-4 py-2 text-right" title="S1: % of all calls that were voicemail / no-answer / silent connect">Gamed %</th>
              <th className="px-4 py-2 text-right" title="S2: % of answered calls that lasted 5–25 seconds">Short %</th>
              <th className="px-4 py-2 text-right" title="S3: % of calls to numbers globally marked dead">Dead %</th>
              <th className="px-4 py-2 text-right" title="S4: AI said no-contact, agent claimed contact">AI mismatch</th>
              <th className="px-4 py-2 text-right" title="S5: how clustered the gamed calls are in time">Burstiness</th>
              <th className="px-4 py-2 text-right" title="S6: SDs above team mean on S1">Peer Z</th>
              <th className="px-4 py-2 text-right">QGS</th>
              <th className="px-4 py-2">Tier</th>
            </tr>
          </thead>
          <tbody>
            {scores.map((row) => {
              const isOpen = expanded === row.agentId;
              return (
                <Fragment key={row.agentId}>
                  <tr
                    className="cursor-pointer border-t border-ink-100 hover:bg-ink-50"
                    onClick={() => setExpanded(isOpen ? null : row.agentId)}
                  >
                    <td className="px-4 py-2 font-medium text-ink-700">{row.agentName}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.totalCalls}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.gamedCalls}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{pct(row.s1GamedRatio)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{pct(row.s2ShortCallSpike)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{pct(row.s3DeadNumberAffinity)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{pct(row.s4AiAgentMismatch)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{pct(row.s5CadenceBurstiness)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.s6PeerZScore.toFixed(2)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold tabular-nums">{row.composite.toFixed(3)}</td>
                    <td className="px-4 py-2"><TierBadge tier={row.tier} /></td>
                  </tr>
                  {isOpen && <AgentDetailRow row={row} />}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── Per-agent expanded detail ────────────────────────────────────────────

function AgentDetailRow({ row }: { row: QualityScoreRow }): JSX.Element {
  return (
    <tr className="border-t border-ink-100 bg-ink-50/50">
      <td colSpan={11} className="px-4 py-4">
        <div className="space-y-4 text-sm text-ink-700">
          <NarrativeBlock row={row} />

          <div className="grid gap-4 lg:grid-cols-2">
            <AgentDailyChart daily={row.daily} agentName={row.agentName} />
            <ClassBreakdown classes={row.classes} total={row.totalCalls} />
          </div>

          <SubScoreExplanations row={row} />
          <EvidenceBlock row={row} />
        </div>
      </td>
    </tr>
  );
}

function NarrativeBlock({ row }: { row: QualityScoreRow }): JSX.Element {
  return (
    <div className="rounded border border-ink-200 bg-white p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Assessment</div>
      <p className="mt-1 leading-relaxed">
        {renderInlineBold(row.narrative)}
      </p>
    </div>
  );
}

// Renders **bold** segments in a narrative string without dangerouslySetInnerHTML.
function renderInlineBold(text: string): JSX.Element[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return <Fragment key={idx}>{part}</Fragment>;
  });
}

function AgentDailyChart({ daily, agentName }: { daily: QualityDailyPoint[]; agentName: string }): JSX.Element {
  const width = Math.max(420, daily.length * 50);
  const height = 200;
  const maxTotal = Math.max(1, ...daily.map((d) => d.total));
  const maxGamed = Math.max(1, ...daily.map((d) => d.gamed));
  return (
    <div className="rounded border border-ink-200 bg-white p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-500">
        {agentName}'s daily pattern
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="h-48 min-w-full">
          {[0, 0.5, 1].map((frac) => {
            const y = 20 + (1 - frac) * (height - 60);
            return (
              <line key={frac} x1={40} x2={width - 10} y1={y} y2={y} stroke="#e5e7eb" strokeWidth={1} />
            );
          })}
          {daily.map((d, i) => {
            const x = 50 + (i * (width - 70)) / Math.max(1, daily.length);
            const barW = Math.max(8, (width - 70) / Math.max(1, daily.length) - 6);
            const totalH = (d.total / maxTotal) * (height - 60);
            const gamedH = (d.gamed / Math.max(maxTotal, maxGamed)) * (height - 60);
            const baseY = height - 40;
            return (
              <g key={d.date}>
                <title>{`${d.date}\nTotal: ${d.total}\nGamed: ${d.gamed} (${pct(d.gamedRatio)})`}</title>
                <rect x={x} y={baseY - totalH} width={barW} height={totalH} fill="#bfdbfe" />
                <rect x={x} y={baseY - gamedH} width={barW} height={gamedH} fill="#ef4444" opacity={0.85} />
                <text x={x + barW / 2} y={height - 24} textAnchor="middle" className="fill-ink-500 text-[10px]">{d.date.slice(5)}</text>
                <text x={x + barW / 2} y={height - 10} textAnchor="middle" className="fill-ink-400 text-[9px]">{d.total === 0 ? '—' : pct(d.gamedRatio)}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <ChartLegend items={[
        { color: '#bfdbfe', label: 'Total calls' },
        { color: '#ef4444', label: 'Gamed calls' },
      ]} />
    </div>
  );
}

function ClassBreakdown({ classes, total }: { classes: QualityClassBreakdown; total: number }): JSX.Element {
  const order: { key: keyof QualityClassBreakdown; label: string; color: string; help: string }[] = [
    { key: 'LIVE',           label: 'Live conversation',         color: '#10b981', help: 'Prospect actually spoke for ≥5 s.' },
    { key: 'NO_ANSWER',      label: 'No answer / busy',          color: '#6366f1', help: 'Never picked up at the signalling layer.' },
    { key: 'VOICEMAIL_AI',   label: 'Voicemail (AI-detected)',   color: '#f59e0b', help: 'AI heard voicemail greeting or agent left no message.' },
    { key: 'MACHINE_AMD',    label: 'Answering machine (AMD)',   color: '#a855f7', help: 'Twilio AMD flagged a machine pickup.' },
    { key: 'RINGOUT_SHORT',  label: 'Ring-out / instant hangup', color: '#f97316', help: 'Answered but <8 s with no real exchange.' },
    { key: 'SILENT_CONNECT', label: 'Silent connect',            color: '#ef4444', help: 'Connected but prospect side never spoke.' },
  ];
  return (
    <div className="rounded border border-ink-200 bg-white p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Breakdown by class</div>
      <ul className="mt-2 space-y-2">
        {order.map(({ key, label, color, help }) => {
          const count = classes[key];
          const ratio = total > 0 ? count / total : 0;
          return (
            <li key={key}>
              <div className="flex items-baseline justify-between text-xs text-ink-700">
                <span title={help} className="font-medium">{label}</span>
                <span className="tabular-nums text-ink-500">{count} · {pct(ratio)}</span>
              </div>
              <div className="mt-0.5 h-2 w-full overflow-hidden rounded bg-ink-100">
                <div className="h-full" style={{ width: `${Math.max(0, ratio * 100)}%`, background: color }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SubScoreExplanations({ row }: { row: QualityScoreRow }): JSX.Element {
  const items: { id: 's1' | 's2' | 's3' | 's4' | 's5' | 's6'; label: string; value: string }[] = [
    { id: 's1', label: 'S1 — Gamed ratio',          value: pct(row.s1GamedRatio) },
    { id: 's2', label: 'S2 — Short-call spike',     value: pct(row.s2ShortCallSpike) },
    { id: 's3', label: 'S3 — Dead-number affinity', value: pct(row.s3DeadNumberAffinity) },
    { id: 's4', label: 'S4 — AI/agent mismatch',    value: pct(row.s4AiAgentMismatch) },
    { id: 's5', label: 'S5 — Cadence burstiness',   value: pct(row.s5CadenceBurstiness) },
    { id: 's6', label: 'S6 — Peer z-score',         value: row.s6PeerZScore.toFixed(2) },
  ];
  return (
    <div className="rounded border border-ink-200 bg-white p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Sub-score explanations</div>
      <ul className="mt-2 space-y-2 text-sm">
        {items.map((item) => (
          <li key={item.id} className="grid gap-1 md:grid-cols-[180px_1fr]">
            <div className="font-medium text-ink-800">
              {item.label} <span className="font-mono text-ink-500">{item.value}</span>
            </div>
            <div className="text-ink-600">{renderInlineBold(row.subScoreNotes[item.id])}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EvidenceBlock({ row }: { row: QualityScoreRow }): JSX.Element {
  const ev = row.evidence;
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <div className="rounded border border-ink-200 bg-white p-3">
        <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Repeated dead numbers</div>
        {ev && ev.topRepeatedDeadNumbers.length > 0 ? (
          <ul className="mt-1 space-y-0.5 font-mono text-xs">
            {ev.topRepeatedDeadNumbers.map((n) => (
              <li key={n.phone}>
                {n.phone} <span className="text-ink-500">× {n.attempts}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-xs text-ink-500">None. Agent isn't repeatedly dialling known-dud numbers.</p>
        )}
      </div>
      <div className="rounded border border-ink-200 bg-white p-3">
        <div className="text-xs font-medium uppercase tracking-wide text-ink-500">End-of-shift spike</div>
        {ev?.endOfShiftSpike ? (
          <div className="mt-1 text-xs">
            <div>First active hour gamed: <strong>{pct(ev.endOfShiftSpike.firstHourGamed)}</strong></div>
            <div>Last active hour gamed: <strong>{pct(ev.endOfShiftSpike.lastHourGamed)}</strong></div>
            <div>Ratio (last ÷ first): <strong>{ev.endOfShiftSpike.ratio.toFixed(2)}×</strong></div>
            <p className="mt-1 text-ink-500">
              {ev.endOfShiftSpike.ratio > 1.5
                ? 'Spike near end of shift — common in agents padding numbers before clock-off.'
                : ev.endOfShiftSpike.ratio < 0.75
                  ? 'Gamed share dropped over the shift — opposite of padding behaviour.'
                  : 'Roughly flat across the shift.'}
            </p>
          </div>
        ) : (
          <p className="mt-1 text-xs text-ink-500">Insufficient hourly data to evaluate.</p>
        )}
      </div>
      <div className="rounded border border-ink-200 bg-white p-3">
        <div className="text-xs font-medium uppercase tracking-wide text-ink-500">Sample gamed call IDs</div>
        {ev && ev.sampleGamedCallIds.length > 0 ? (
          <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-ink-600">
            {ev.sampleGamedCallIds.slice(0, 8).map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-xs text-ink-500">No gamed calls in window.</p>
        )}
      </div>
    </div>
  );
}

function TierBadge({ tier }: { tier: QualityTier }): JSX.Element {
  const styles: Record<QualityTier, string> = {
    green: 'bg-emerald-100 text-emerald-800',
    yellow: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[tier]}`}>
      {tier}
    </span>
  );
}

function EmptyState({ skipped }: { skipped: number }): JSX.Element {
  return (
    <div className="card p-6 text-center text-sm text-ink-600">
      <p className="font-medium text-ink-700">No scoreable agents in this window.</p>
      <p className="mt-1">
        {skipped > 0
          ? `${skipped} agent(s) had calls but fewer than 20 — too few to score reliably. Widen the date range to include them.`
          : 'There are no outbound calls in this window.'}
      </p>
    </div>
  );
}

function pct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}
